// Eager slim slice of the preview function barrel (#16 preview half).
//
// The full `./index.js` barrel re-exports the whole preview function surface —
// the Scene iframe boot (`renderProject` + `renderDesignSystem`), the Simone
// assistant transport (`bootPreviewAssistant` / `installPreviewAiTransport` /
// `reloadPreviewProject` / `newSession` / `setModelMode`), `sendPrompt`, and the
// element picker (`togglePick` / `inspectSelected`) — that the unified shell
// dragged into its EAGER chunk via `import * from '../preview/functions/index.js'`
// in workspace/context.js.
//
// Preview is now a lazy sub-app: the `/preview` ROUTE (workspace/pages/index.js
// → lazySharedLib) and the `/preview` PageSlot (home AppWindow + canvas
// `_canvasSlot`, resolved through _APP_WINDOW_PAGES._previewPage in
// workspace/pages/main.js → lazySharedLibPage) both FOLD the full preview
// context (components + functions) into the live shell context on first mount.
// So every preview function reached from INSIDE the preview page — chiefly
// `renderProject` + `renderDesignSystem`, whose only callers are preview-internal
// (Scene.js el.call('renderProject') + previewAiTransport.js reloadPreviewProject)
// — rides the fold: caller (Scene) and callee (renderProject) both arrive together,
// registered BEFORE the page mounts. None of that needs to be eager.
//
// The eager chunk only needs the preview functions the shell references BEFORE any
// /preview fold. A grep of the whole eager surface (workspace app.js/state/pages/
// components/functions + shared/**) found these referenced ONLY by the shared
// Simone `Chat.js` (mounted by the shell's AssistantDrawer on every route):
//   • togglePick / inspectSelected — Chat's PickBtn/InspectZeroBtn el.call these.
//   • setModelMode — Chat's model-mode dropdown el.calls this (best-effort).
//   (fetchAiProviderDiagnostics is shared's own registration, not preview's.)
// `sendPrompt` and `newSession` are NOT here: the shell defines its OWN
// (workspace/functions/_simone/sendPrompt.js + sessions.js), spread LAST into
// context.functions so they win — preview's same-named exports are shadowed both
// before and after this carve (add-missing fold never overrides them).
//
// preview/package.json has no `sideEffects: false`, so re-exporting through
// `./index.js` would NOT tree-shake (it would evaluate the whole barrel, pulling
// the Scene/renderProject boot code back in). Each key below is therefore
// `export *`-ed from its SOURCE file directly. Both source files are light:
// pickElement.js has ZERO imports; previewAiTransport.js's only top-level import
// is aiService.js (already eager — the shell's simoneAiTransport/homeAiSend use
// it), and its `renderProject` reference is a runtime `this.call`, not a static
// import edge — so neither drags the preview render pipeline into the eager chunk.
export { togglePick, inspectSelected, previewPickArmedOn } from './_simone/pickElement.js'
export { setModelMode } from './_simone/previewAiTransport.js'
// The AI-answer views' provider, site resolution, addresses and reads
// (`site-preview`, `project-pages`, `design-system`): the shell reads the
// provider on ANY route (workspace functions/assistantAppViews.js) and the
// views mount without a fold. No static imports (channels, the project read
// and the design-system kit are dynamic imports inside the calls that need
// them).
export * from './previewAnswer.js'
export * from './previewPagesAnswer.js'
// THE SITE BUILDER: the shell transport resolves the site on screen per send
// and hands every tool frame to previewAfterAiWrite on ANY route, and the
// shell's assistantOpenSiteBuilder opens the builder before any /preview
// fold. No static imports (the shared project read is reached by el.call,
// else a dynamic import inside the call).
export * from './previewSite.js'
